import {useCallback, useRef} from 'react';
import {useMutation} from '@common-fe/common-fe';

import {api} from '@/api';
import {API_URL} from '@/dtos';
import {OcrDocumentResponse, OpenaiOcrDocument} from '@/modules/UseMyMoney/UseMyMoney.types';

interface Response extends Pick<OcrDocumentResponse, 'warnings'> {
  ai_approved?: boolean;
}

interface Payload {
  fileId: string,
  claimId?: string
}

const useGetDocumentsOpenaiOcrQuery = () => {
  const controllerRef = useRef<AbortController | null>(null);

  const { mutateAsync, isLoading } = useMutation((payload: Payload) => {
    controllerRef.current = new AbortController();
    return api.get<Response>(
      API_URL.DOCUMENTS_FILES_OPENAI_OCR_MATCHING_BY_FILE_ID(payload?.fileId),
      {
        signal: controllerRef.current.signal,
        params: {
          claim_id: payload?.claimId,
        }
      }
    );
  });

  const handleGetDocumentOcr = useCallback(
    async (payload: Payload): Promise<OpenaiOcrDocument | undefined> => {
      if (!payload?.claimId) {
        return;
      }

      const response = await mutateAsync(payload);

      if (!response?.data) {
        return;
      }

      return {
        aiApproved: response?.data?.ai_approved,
      };
    }, [mutateAsync]);

  const cancelOpenaiOcr = useCallback(() => {
    if (!controllerRef.current) {
      controllerRef.current = new AbortController();
    }
    controllerRef.current.abort();
  }, []);

  return {
    getDocumentOpenaiOcr: handleGetDocumentOcr,
    cancelOpenaiOcr,
    isOcrLoading: isLoading,
  };
};

export default useGetDocumentsOpenaiOcrQuery;
